import { DetailProfile } from "@/components/layout/DetailProfile";
import { DetailRightPanel } from "@/v5/components/DetailRightPanel";
import { ContactInfoHeader } from "@/components/patients/ContactInfoHeader";
import { LeadDetailModal } from "@/pages/leads/LeadDetailModal";
import { FilterSidebar } from "@/components/layout/FilterSidebar";
import { usePatientDetail } from "@/v5/hooks/usePatientDetail";
import { useQuery } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { formatCount, splitRawContent } from "@/lib/utils";
import { getLead } from "@/services/leads.service";
import { getCRMPipelineStageMap } from "@/services/crm.service";
import { listPlanTypes } from "@/services/admin.service";
import {
  useCRMStages,
  useCRMPlans,
  type CRMPlan,
  type CRMStage,
} from "@/v5/hooks/useCRM";
import { useCRMLeads, type Lead } from "@/v5/hooks/useLeads";
import { useEffectiveFacilityIds, useIsFacilityLocked } from "@/hooks/useGlobalFacility";
import { buildApiFilterString, useFilters } from "@/v5/hooks/useFilters";
import { ListPageLayout } from "@/v5/layouts/ListPageLayout";
import type { FilterConfig } from "@/v5/types/filters";
import { cn } from "@/v5/lib/utils";
import {
  AlertTriangle,
  Calendar,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Flame,
  LayoutGrid,
  List,
  Loader2,
  Mail,
  MapPin,
  Phone,
  User,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/v5/components/ui/button";
import { useLocation } from "@/v5/lib/wouter-compat";

const PAGE_URL = "v5-crm";

const FILTER_CONFIGS: FilterConfig[] = [
  { key: "daterange", label: "Date", type: "daterange" },
  { key: "lead-status", label: "Lead Status", type: "select", allLabel: "All Statuses", multi: true },
  { key: "lead-assign", label: "Assign Status", type: "select", allLabel: "All", multi: true },
  { key: "lead-source", label: "Lead Source", type: "select", allLabel: "All Sources", multi: true },
  { key: "utm-source", label: "UTM Source", type: "select", allLabel: "All UTM Sources", multi: true },
  { key: "utm-campaign", label: "Campaign", type: "select", allLabel: "All Campaigns", multi: true },
  { key: "plan-type", label: "Plan Type", type: "select", allLabel: "All Plan Types", multi: true },
  { key: "facility", label: "Facility", type: "select", allLabel: "All Facilities", multi: true },
  { key: "users", label: "User", type: "select", allLabel: "All Users", multi: true },
  { key: "groups", label: "Group", type: "select", allLabel: "All Groups", multi: true },
];

/** Days elapsed since `dateStr`, or null when it is absent/unparseable. */
function daysSince(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  // Legacy rows carry '0000-00-00 00:00:00' (imported accounts), which parses
  // to NaN and would otherwise render "NaNd ago" behind a red warning icon.
  if (Number.isNaN(date.getTime())) return null;
  return Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
}

function timeAgo(dateStr: string): string {
  const diffDays = daysSince(dateStr);
  if (diffDays === null) return "";
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "1d ago";
  if (diffDays < 30) return `${diffDays}d ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths}mo ago`;
  return `${Math.floor(diffMonths / 12)}y ago`;
}

/** Compact duration for the "in stage" readout: "Today", "12d", "3mo", "2y". */
function duration(dateStr: string | null | undefined): string {
  const diffDays = daysSince(dateStr);
  if (diffDays === null) return "";
  if (diffDays === 0) return "Today";
  if (diffDays < 30) return `${diffDays}d`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths}mo`;
  return `${Math.floor(diffMonths / 12)}y`;
}

/**
 * `plans.lcType` values that mean the stored `lastContact` really was contact
 * with the patient. Anything else (notably 'stage', and '' on imported rows)
 * means the timestamp was left there by a stage move, so it must not be shown
 * as a contact.
 *
 * Do NOT add 'stage' here. It is ambiguous, not merely stage-only: legacy
 * events_log_processor_tk.php:886 also stamps 'stage' when a staff member
 * replies inside an event-log thread, which IS real outreach. That is why the
 * absence of a contact-type value renders nothing rather than a claim like
 * "no contact logged" — we cannot tell the two apart from the plan row. (v5's
 * own reply path stamps 'comm', so this is a shrinking legacy artifact.)
 */
const CONTACT_LC_TYPES = new Set(["sms", "email", "comm", "none", "whatsapp"]);

function hasRealContact(plan: Pick<CRMPlan, "lastContact" | "lcType">): boolean {
  return (
    daysSince(plan.lastContact) !== null &&
    CONTACT_LC_TYPES.has((plan.lcType ?? "").trim().toLowerCase())
  );
}

function InterestFlame({ level }: { level: string }) {
  const n = Math.min(3, Math.max(0, parseInt(level) || 0));
  if (n === 0) return null;
  return (
    <span className="inline-flex gap-0" title={`Interest level ${n}`}>
      {Array.from({ length: n }).map((_, i) => (
        <Flame
          key={i}
          className="h-3.5 w-3.5 fill-orange-400 text-orange-500"
        />
      ))}
    </span>
  );
}

/**
 * Age-based emphasis, shared by the "in stage" and "last contact" readouts so
 * both use one set of thresholds: up to 7d muted, up to 14d amber, older red
 * and flagged.
 */
function staleness(dateStr: string | null | undefined): {
  className: string;
  stale: boolean;
} {
  const diffDays = daysSince(dateStr);
  if (diffDays === null || diffDays <= 7) {
    return { className: "text-muted-foreground", stale: false };
  }
  if (diffDays <= 14) return { className: "text-amber-600", stale: false };
  return { className: "text-red-600", stale: true };
}

function formatReceivedDate(dateStr: string): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function joinAddress(
  address: string | null | undefined,
  city: string | null | undefined,
  state: string | null | undefined,
  zip: string | null | undefined,
): string | null {
  const parts = [address, city, state].filter((p): p is string => !!p && p.trim() !== "");
  if (parts.length === 0 && !zip) return null;
  const base = parts.join(", ");
  return zip ? (base ? `${base} ${zip}` : zip) : base || null;
}

const PLAN_TYPE_COLORS: Record<string, string> = {};
const COLOR_PALETTE = [
  "bg-blue-100 text-blue-800",
  "bg-emerald-100 text-emerald-800",
  "bg-violet-100 text-violet-800",
  "bg-amber-100 text-amber-800",
  "bg-rose-100 text-rose-800",
  "bg-cyan-100 text-cyan-800",
  "bg-fuchsia-100 text-fuchsia-800",
  "bg-lime-100 text-lime-800",
  "bg-orange-100 text-orange-800",
  "bg-teal-100 text-teal-800",
];
let nextColorIdx = 0;

function getPlanTypeColor(planType: string): string {
  if (!planType) return "bg-muted text-muted-foreground";
  if (!PLAN_TYPE_COLORS[planType]) {
    PLAN_TYPE_COLORS[planType] = COLOR_PALETTE[nextColorIdx % COLOR_PALETTE.length];
    nextColorIdx++;
  }
  return PLAN_TYPE_COLORS[planType];
}

function LeadCard({
  lead,
  isSelected,
  onClick,
}: {
  lead: Lead;
  isSelected: boolean;
  onClick: () => void;
}) {
  const interests = lead.interest
    ? lead.interest.split(",").map((s) => s.trim()).filter(Boolean)
    : [];

  return (
    <div
      className={cn(
        "border rounded-lg p-3 cursor-pointer transition-all hover:shadow-md",
        isSelected
          ? "border-primary bg-primary/5 shadow-sm"
          : "border-border bg-card hover:border-primary/30",
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-2 mb-1.5">
        <div className="flex items-center gap-1 min-w-0">
          <span className="font-medium text-sm text-foreground truncate">
            {lead.firstName} {lead.lastName}
          </span>
          <InterestFlame level={lead.interest ? "1" : "0"} />
        </div>
      </div>

      {lead.planType && (
        <span
          className={cn(
            "inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full mb-2",
            getPlanTypeColor(lead.planType),
          )}
        >
          {lead.planType}
        </span>
      )}

      {interests.length > 0 && (
        <div className="mb-2">
          <div className="text-[10px] text-muted-foreground mb-1">Interested in:</div>
          <div className="flex flex-wrap gap-1">
            {interests.map((interest) => (
              <span
                key={interest}
                className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700"
              >
                {interest}
              </span>
            ))}
          </div>
        </div>
      )}

      {lead.leadSource && (
        <div className="text-xs text-muted-foreground mb-0.5 truncate">
          Source: <span className="font-medium text-foreground">{lead.leadSource}</span>
        </div>
      )}
      {(lead.utmSource || lead.utmCampaign) && (
        <div className="text-xs text-muted-foreground mb-1 truncate">
          UTM: <span className="font-medium text-foreground">
            {[lead.utmSource, lead.utmCampaign].filter(Boolean).join(" / ")}
          </span>
        </div>
      )}

      {lead.contactEmail && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-0.5 truncate">
          <Mail className="h-3 w-3 shrink-0" />
          <span className="truncate">{lead.contactEmail}</span>
        </div>
      )}
      {lead.contactPhone && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1 truncate">
          <Phone className="h-3 w-3 shrink-0" />
          <span>{lead.contactPhone}</span>
        </div>
      )}

      {lead.contactDate && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-0.5">
          <Calendar className="h-3 w-3 shrink-0" />
          <span>Received: {formatReceivedDate(lead.contactDate)}</span>
        </div>
      )}

      <div className="flex items-center gap-1 text-xs text-muted-foreground mb-0.5">
        <User className="h-3 w-3 shrink-0" />
        <span className="truncate">Assigned: {lead.assignedTo || "Unassigned"}</span>
      </div>
    </div>
  );
}

function LeadRow({
  lead,
  isSelected,
  onClick,
}: {
  lead: Lead;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 px-3 py-2 border-b border-border/50 cursor-pointer transition-colors text-xs",
        isSelected ? "bg-primary/5" : "hover:bg-muted/50",
      )}
      onClick={onClick}
    >
      <span className="font-medium text-foreground truncate min-w-[100px] max-w-[130px]">
        {lead.firstName} {lead.lastName}
      </span>
      {lead.planType && (
        <span
          className={cn(
            "text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap",
            getPlanTypeColor(lead.planType),
          )}
        >
          {lead.planType}
        </span>
      )}
      {lead.leadSource && (
        <span className="text-muted-foreground truncate max-w-[80px]">{lead.leadSource}</span>
      )}
      {/* The inquiry date, not a contact — see LeadCard's "Received:" line. */}
      <span
        className="whitespace-nowrap ml-auto text-muted-foreground"
        title={lead.contactDate ? `Received ${formatReceivedDate(lead.contactDate)}` : undefined}
      >
        {lead.contactDate ? timeAgo(lead.contactDate) : "—"}
      </span>
    </div>
  );
}

function PlanCard({
  plan,
  isSelected,
  onClick,
}: {
  plan: CRMPlan;
  isSelected: boolean;
  onClick: () => void;
}) {
  const procedures = plan.procName
    ? plan.procName.split(",").map((p) => p.trim()).filter(Boolean)
    : [];

  // Two separate facts, deliberately not merged: plans.lastContact is stamped
  // by real outreach AND by every stage change, so it can only be shown as a
  // contact when lcType says it is one. Either half is omitted when unknown.
  const inStage = duration(plan.stageSince);
  const stageStale = staleness(plan.stageSince);
  const contactShown = hasRealContact(plan);
  const contactStale = staleness(contactShown ? plan.lastContact : null);

  return (
    <div
      className={cn(
        "border rounded-lg p-3 cursor-pointer transition-all hover:shadow-md",
        isSelected
          ? "border-primary bg-primary/5 shadow-sm"
          : "border-border bg-card hover:border-primary/30",
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-2 mb-1.5">
        <div className="flex items-center gap-1 min-w-0">
          <span className="font-medium text-sm text-foreground truncate">
            {plan.patientLastName}, {plan.patientFirstName}
          </span>
          <InterestFlame level={plan.interestLevel} />
        </div>
      </div>

      {plan.facility && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
          <MapPin className="h-3 w-3 shrink-0" />
          <span className="truncate">{plan.facility}</span>
        </div>
      )}

      {plan.planTypeName && (
        <span
          className={cn(
            "inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full mb-2",
            getPlanTypeColor(plan.planTypeName),
          )}
        >
          {plan.planTypeName}
        </span>
      )}

      {procedures.length > 0 && (
        <div className="mb-2">
          <div className="text-[10px] text-muted-foreground mb-1">Interested in:</div>
          <div className="flex flex-wrap gap-1">
            {procedures.map((proc) => (
              <span
                key={proc}
                className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700"
              >
                {proc}
              </span>
            ))}
          </div>
        </div>
      )}

      {plan.patientEmail && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-0.5 truncate">
          <Mail className="h-3 w-3 shrink-0" />
          <span className="truncate">{plan.patientEmail}</span>
        </div>
      )}
      {plan.patientPhone && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1 truncate">
          <Phone className="h-3 w-3 shrink-0" />
          <span>{plan.patientPhone}</span>
        </div>
      )}

      <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
        <User className="h-3 w-3 shrink-0" />
        <span className="truncate">Assigned: {plan.coordName || "Unassigned"}</span>
      </div>

      {(inStage || contactShown) && (
        <div className="flex items-center gap-1 text-[10px] mt-1.5 pt-1.5 border-t border-border/50">
          {(stageStale.stale || contactStale.stale) && (
            <AlertTriangle className="h-3 w-3 shrink-0 text-red-600" />
          )}
          {inStage && (
            <span className={stageStale.className}>In stage: {inStage}</span>
          )}
          {inStage && contactShown && <span className="text-muted-foreground">·</span>}
          {contactShown && (
            <span className={contactStale.className}>
              Last contact: {timeAgo(plan.lastContact)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function PlanRow({
  plan,
  isSelected,
  onClick,
}: {
  plan: CRMPlan;
  isSelected: boolean;
  onClick: () => void;
}) {
  // The row lives in a 300px column already carrying name, flames, plan type
  // and facility, so only the in-stage duration is shown; the tooltip carries
  // the full breakdown including last contact.
  const inStage = duration(plan.stageSince);
  const stageStale = staleness(plan.stageSince);
  const contactShown = hasRealContact(plan);
  const tooltip = [
    inStage ? `In stage: ${inStage}` : null,
    contactShown ? `Last contact: ${timeAgo(plan.lastContact)}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className={cn(
        "flex items-center gap-2 px-3 py-2 border-b border-border/50 cursor-pointer transition-colors text-xs",
        isSelected ? "bg-primary/5" : "hover:bg-muted/50",
      )}
      onClick={onClick}
    >
      <span className="font-medium text-foreground truncate min-w-[100px] max-w-[130px]">
        {plan.patientLastName}, {plan.patientFirstName}
      </span>
      <InterestFlame level={plan.interestLevel} />
      {plan.planTypeName && (
        <span
          className={cn(
            "text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap",
            getPlanTypeColor(plan.planTypeName),
          )}
        >
          {plan.planTypeName}
        </span>
      )}
      {plan.facility && (
        <span className="text-muted-foreground truncate max-w-[80px]">{plan.facility}</span>
      )}
      <span
        className={cn("whitespace-nowrap ml-auto", inStage ? stageStale.className : "text-muted-foreground")}
        title={tooltip || undefined}
      >
        {inStage || "—"}
      </span>
    </div>
  );
}

function NewLeadsColumn({
  leadsCount,
  filtersArray,
  filterPlus,
  selectedLeadId,
  onSelectLead,
}: {
  leadsCount: number;
  filtersArray: string;
  filterPlus: string;
  selectedLeadId: string | null;
  onSelectLead: (lead: Lead) => void;
}) {
  const [viewMode, setViewMode] = useState<"card" | "list">("card");
  const [collapsed, setCollapsed] = useState(false);
  const { leads, totalCount, loading, loadingMore, hasMore, loadMore } =
    useCRMLeads(!collapsed, filtersArray, filterPlus);

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || loadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loadMore]);

  const displayCount = formatCount(totalCount > 0 ? totalCount : leadsCount);
  const loadedLabel =
    leads.length > 0 && totalCount > 0
      ? `${formatCount(leads.length)} of ${formatCount(totalCount)}`
      : null;

  return (
    <div
      className={cn(
        "flex flex-col border rounded-lg bg-muted/30 shrink-0 transition-all",
        collapsed ? "w-14" : "w-[300px]",
      )}
    >
      <div
        className={cn(
          "flex items-center gap-2 px-3 py-2.5 border-b rounded-t-lg",
          "bg-muted/60",
          collapsed && "flex-col py-3",
        )}
      >
        <button
          className="shrink-0 text-muted-foreground hover:text-foreground"
          onClick={() => setCollapsed((c) => !c)}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>

        {collapsed ? (
          <div className="flex-1 min-w-0">
            <span className="text-xs font-semibold text-foreground" style={{ writingMode: "vertical-lr" }}>
              New Lead
            </span>
            <div className="mt-2 text-[10px] text-muted-foreground font-medium text-center">
              {displayCount}
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-foreground truncate block">
                New Lead
              </span>
            </div>
            <span className="text-xs text-muted-foreground font-medium tabular-nums">
              {displayCount}
            </span>
            <div className="flex items-center border rounded bg-background">
              <button
                className={cn(
                  "p-1 rounded-l transition-colors",
                  viewMode === "card"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
                onClick={() => setViewMode("card")}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
              </button>
              <button
                className={cn(
                  "p-1 rounded-r transition-colors",
                  viewMode === "list"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
                onClick={() => setViewMode("list")}
              >
                <List className="h-3.5 w-3.5" />
              </button>
            </div>
          </>
        )}
      </div>

      {!collapsed && (
        <div className="flex-1 min-h-0 overflow-y-auto p-2">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-8 text-xs text-muted-foreground">
              No leads
            </div>
          ) : viewMode === "card" ? (
            <div className="flex flex-col gap-2">
              {leads.map((lead) => (
                <LeadCard
                  key={lead.contactID}
                  lead={lead}
                  isSelected={selectedLeadId === lead.contactID}
                  onClick={() => onSelectLead(lead)}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col">
              {leads.map((lead) => (
                <LeadRow
                  key={lead.contactID}
                  lead={lead}
                  isSelected={selectedLeadId === lead.contactID}
                  onClick={() => onSelectLead(lead)}
                />
              ))}
            </div>
          )}

          {loadingMore && (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {hasMore && <div ref={sentinelRef} className="h-1 shrink-0" />}
          {loadedLabel && (
            <div className="text-center py-1.5 text-[10px] text-muted-foreground">
              {loadedLabel}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StageColumn({
  stage,
  selectedPlanId,
  onSelectPlan,
  filtersArray,
  filterPlus,
}: {
  stage: CRMStage;
  selectedPlanId: string | null;
  onSelectPlan: (plan: CRMPlan) => void;
  filtersArray: string;
  filterPlus: string;
}) {
  const [viewMode, setViewMode] = useState<"card" | "list">("card");
  const [collapsed, setCollapsed] = useState(false);
  const { plans, totalCount, loading, loadingMore, hasMore, loadMore } =
    useCRMPlans(stage.accountCustomStageID, stage.stageID, !collapsed, filtersArray, filterPlus);

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || loadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loadMore]);

  const loadedLabel =
    plans.length > 0 && totalCount > 0
      ? `${formatCount(plans.length)} of ${formatCount(totalCount)}`
      : null;

  return (
    <div
      className={cn(
        "flex flex-col border rounded-lg bg-muted/30 shrink-0 transition-all",
        collapsed ? "w-14" : "w-[300px]",
      )}
    >
      <div
        className={cn(
          "flex items-center gap-2 px-3 py-2.5 border-b bg-muted/60 rounded-t-lg",
          collapsed && "flex-col py-3",
        )}
      >
        <button
          className="shrink-0 text-muted-foreground hover:text-foreground"
          onClick={() => setCollapsed((c) => !c)}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>

        {collapsed ? (
          <div className="flex-1 min-w-0">
            <span className="text-xs font-semibold text-foreground" style={{ writingMode: "vertical-lr" }}>
              {stage.stageName}
            </span>
            <div className="mt-2 text-[10px] text-muted-foreground font-medium text-center">
              {stage.planCount}
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-foreground truncate block">
                {stage.stageName}
              </span>
            </div>
            <span className="text-xs text-muted-foreground font-medium tabular-nums">
              {stage.planCount}
            </span>
            <div className="flex items-center border rounded bg-background">
              <button
                className={cn(
                  "p-1 rounded-l transition-colors",
                  viewMode === "card"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
                onClick={() => setViewMode("card")}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
              </button>
              <button
                className={cn(
                  "p-1 rounded-r transition-colors",
                  viewMode === "list"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
                onClick={() => setViewMode("list")}
              >
                <List className="h-3.5 w-3.5" />
              </button>
            </div>
          </>
        )}
      </div>

      {!collapsed && (
        <div className="flex-1 min-h-0 overflow-y-auto p-2">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : plans.length === 0 ? (
            <div className="text-center py-8 text-xs text-muted-foreground">
              No plans
            </div>
          ) : viewMode === "card" ? (
            <div className="flex flex-col gap-2">
              {plans.map((plan) => (
                <PlanCard
                  key={plan.planID}
                  plan={plan}
                  isSelected={selectedPlanId === plan.planID}
                  onClick={() => onSelectPlan(plan)}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col">
              {plans.map((plan) => (
                <PlanRow
                  key={plan.planID}
                  plan={plan}
                  isSelected={selectedPlanId === plan.planID}
                  onClick={() => onSelectPlan(plan)}
                />
              ))}
            </div>
          )}

          {loadingMore && (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {hasMore && <div ref={sentinelRef} className="h-1 shrink-0" />}
          {loadedLabel && (
            <div className="text-center py-1.5 text-[10px] text-muted-foreground">
              {loadedLabel}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

type Selection =
  | { type: "plan"; plan: CRMPlan }
  | { type: "lead"; lead: Lead }
  | null;

function hasVisibleStageName(stage: CRMStage): boolean {
  const name = (stage.stageName ?? "").trim();
  return name !== "" && name.toLowerCase() !== "none";
}

export default function CRMPage() {
  const [selection, setSelection] = useState<Selection>(null);
  const [panelExpandTrigger, setPanelExpandTrigger] = useState(0);
  const [leadModalId, setLeadModalId] = useState<number | null>(null);
  const [, setLocation] = useLocation();

  const filters = useFilters(PAGE_URL, FILTER_CONFIGS);
  const localFacilityIds = Array.isArray(filters.values.facility)
    ? (filters.values.facility as string[])
    : [];
  const effectiveFacilityIds = useEffectiveFacilityIds(localFacilityIds);
  const isFacilityLocked = useIsFacilityLocked();

  const effectiveValues = useMemo(() => {
    if (!isFacilityLocked) return filters.values;
    return {
      ...filters.values,
      facility: effectiveFacilityIds,
    };
  }, [filters.values, isFacilityLocked, effectiveFacilityIds]);

  const { filtersArray, filterPlus } = useMemo(
    () => buildApiFilterString(effectiveValues, filters.negations),
    [effectiveValues, filters.negations],
  );
  const { stages, newLeadsCount, loading } = useCRMStages(filtersArray, filterPlus);

  const { data: planTypes } = useQuery({
    queryKey: ["admin", "plan-types"],
    queryFn: listPlanTypes,
    staleTime: 5 * 60_000,
  });

  const { data: pipelineStageMapData } = useQuery({
    queryKey: ["crm-pipeline-stage-map"],
    queryFn: getCRMPipelineStageMap,
    staleTime: 5 * 60_000,
  });

  const selectedPatientId =
    selection?.type === "plan" ? selection.plan.patientID : null;

  const { detail, loading: detailLoading } = usePatientDetail(selectedPatientId);

  const selectedLeadId =
    selection?.type === "lead" ? Number(selection.lead.contactID) : null;

  const { data: leadDetail } = useQuery({
    queryKey: ["lead-detail", selectedLeadId],
    queryFn: () => getLead(selectedLeadId!),
    enabled: !!selectedLeadId && Number.isFinite(selectedLeadId),
    staleTime: 60_000,
  });

  const handleSelectPlan = useCallback((plan: CRMPlan) => {
    setSelection({ type: "plan", plan });
    setPanelExpandTrigger((n) => n + 1);
  }, []);

  const handleSelectLead = useCallback((lead: Lead) => {
    setSelection({ type: "lead", lead });
    setPanelExpandTrigger((n) => n + 1);
  }, []);

  const visibleStages = useMemo(() => {
    const named = stages.filter(hasVisibleStageName);
    const selectedPlanTypes = effectiveValues["plan-type"];
    if (!Array.isArray(selectedPlanTypes) || selectedPlanTypes.length === 0) {
      return named;
    }

    if (!planTypes || !pipelineStageMapData) {
      return named;
    }

    const pipelineMap = pipelineStageMapData.pipelineStageMap;

    const allowedStageIds = new Set<string>();
    for (const ptId of selectedPlanTypes) {
      const pt = planTypes.find((p) => String(p.planTypeId) === ptId);
      const pipelineId = pt ? String(pt.pipelineTypeId) : "0";

      if (pipelineId === "0") {
        return named;
      }

      const stageIds = pipelineMap[pipelineId];
      if (!stageIds) {
        return named;
      }
      for (const sid of stageIds) {
        allowedStageIds.add(String(sid));
      }
    }

    return named.filter((s) => allowedStageIds.has(s.accountCustomStageID));
  }, [stages, effectiveValues, planTypes, pipelineStageMapData]);
  const totalPlans = visibleStages.reduce((sum, s) => sum + s.planCount, 0);

  return (
    <>
    <ListPageLayout
      loading={loading}
      filterSidebar={
        <FilterSidebar
          filters={FILTER_CONFIGS}
          values={effectiveValues}
          options={filters.options}
          negations={filters.negations}
          onChange={filters.setFilter}
          onNegateChange={filters.setNegation}
          onClear={filters.clearFilter}
          lockedKeys={isFacilityLocked ? ["facility"] : undefined}
          storageKey="crm-pipeline"
        />
      }
      rightPanel={
        selection ? (
          <DetailRightPanel expandTrigger={panelExpandTrigger}>
            <div className="flex flex-col h-full">
              {selection.type === "plan" && (
                <div className="flex items-center justify-end gap-1 border-b border-border px-3 py-1.5">
                  <Button variant="ghost" size="sm" className="h-7"
                    onClick={() => setLocation(`/patients/${selection.plan.patientID}`)}>
                    <ExternalLink className="h-3.5 w-3.5 mr-1" />
                    Open
                  </Button>
                </div>
              )}
              <div className="flex-1 min-h-0">
                {selection.type === "plan" ? (
                  <DetailProfile detail={detail} loading={detailLoading}
                    onSectionNavigate={(section) =>
                      selection.plan.patientID && setLocation(`/patients/${selection.plan.patientID}?section=${section}`)
                    }
                  />
                ) : (
                  <div className="p-4 flex flex-col gap-4 h-full min-h-0">
                    <ContactInfoHeader
                      id={Number(selection.lead.contactID) || 0}
                      name={
                        `${leadDetail?.firstName ?? selection.lead.firstName ?? ""} ${
                          leadDetail?.lastName ?? selection.lead.lastName ?? ""
                        }`.trim() || "Lead"
                      }
                      phone={
                        leadDetail?.cellPhone ??
                        leadDetail?.phone ??
                        (selection.lead.contactPhone || null)
                      }
                      email={leadDetail?.email ?? (selection.lead.contactEmail || null)}
                      address={
                        leadDetail
                          ? joinAddress(
                              leadDetail.address,
                              leadDetail.city,
                              leadDetail.state,
                              leadDetail.zip,
                            )
                          : null
                      }
                      birthDate={leadDetail?.birthDate ?? null}
                      extraFields={leadDetail?.contactsRecord ?? []}
                    />

                    <div className="h-px bg-slate-200" />

                    <div className="relative overflow-hidden" style={{ maxHeight: "18rem" }}>
                      <div className="space-y-2 text-sm">
                        {(() => {
                          // Subject comes from the short `contacts.topics` label.
                          // The long email body is rendered separately under "Message".
                          const subject = leadDetail?.subject ?? selection.lead.subject ?? "";
                          // Prefer server-split preview; fall back to client-side split on the cached message.
                          const preview =
                            leadDetail?.messagePreview ??
                            selection.lead.messagePreview ??
                            splitRawContent(leadDetail?.message).preview;
                          const previewHasHtml = typeof preview === "string" && preview.includes("<");
                          return (
                            <>
                              {subject && (
                                <div>
                                  <span className="text-xs text-muted-foreground">Subject</span>
                                  <p className="text-foreground">{subject}</p>
                                </div>
                              )}
                              {preview && (
                                <div>
                                  <span className="text-xs text-muted-foreground">Message</span>
                                  {previewHasHtml ? (
                                    <div
                                      className="text-foreground text-sm prose prose-sm max-w-none [&_table]:text-xs [&_table]:border-collapse [&_td]:p-1 [&_td]:border [&_td]:border-border"
                                      dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(preview),
                                      }}
                                    />
                                  ) : (
                                    <p className="whitespace-pre-line text-foreground">{preview}</p>
                                  )}
                                </div>
                              )}
                            </>
                          );
                        })()}
                        {leadDetail?.contactsRecord && leadDetail.contactsRecord.length > 0 && (
                          <div>
                            <span className="text-xs text-muted-foreground">Additional Info</span>
                            <ul className="mt-0.5 space-y-0.5">
                              {leadDetail.contactsRecord.slice(0, 3).map((row, idx) => (
                                <li key={`${row.label}-${idx}`} className="text-foreground">
                                  <span className="font-medium">{row.label || "—"}:</span>{" "}
                                  <span className="text-muted-foreground">{row.value}</span>
                                </li>
                              ))}
                              {leadDetail.contactsRecord.length > 3 && (
                                <li className="text-xs text-muted-foreground">
                                  +{leadDetail.contactsRecord.length - 3} more
                                </li>
                              )}
                            </ul>
                          </div>
                        )}
                        {selection.lead.leadSource && (
                          <div>
                            <span className="text-xs text-muted-foreground">Lead Source</span>
                            <p className="text-foreground">{selection.lead.leadSource}</p>
                          </div>
                        )}
                        {selection.lead.utmSource && (
                          <div>
                            <span className="text-xs text-muted-foreground">UTM Source</span>
                            <p className="text-foreground">{selection.lead.utmSource}</p>
                          </div>
                        )}
                        {selection.lead.utmCampaign && (
                          <div>
                            <span className="text-xs text-muted-foreground">UTM Campaign</span>
                            <p className="text-foreground">{selection.lead.utmCampaign}</p>
                          </div>
                        )}
                        {selection.lead.origin && (
                          <div>
                            <span className="text-xs text-muted-foreground">Origin</span>
                            <p className="text-foreground">{selection.lead.origin}</p>
                          </div>
                        )}
                        {selection.lead.assignedTo && (
                          <div>
                            <span className="text-xs text-muted-foreground">Assigned To</span>
                            <p className="text-foreground">{selection.lead.assignedTo}</p>
                          </div>
                        )}
                        {selection.lead.contactDate && (
                          <div>
                            <span className="text-xs text-muted-foreground">Received</span>
                            <p className="text-foreground">{formatReceivedDate(selection.lead.contactDate)}</p>
                          </div>
                        )}
                      </div>
                      <div className="pointer-events-none absolute bottom-0 inset-x-0 h-12 bg-gradient-to-t from-white to-transparent" />
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        const id = Number(selection.lead.contactID);
                        if (Number.isFinite(id) && id > 0) setLeadModalId(id);
                      }}
                    >
                      View full details
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </DetailRightPanel>
        ) : undefined
      }
    >
      <div className="h-full flex flex-col">
        <div className="px-4 py-3 border-b border-border shrink-0">
          <h1 className="text-lg font-semibold text-foreground">CRM Pipeline</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {newLeadsCount} leads &middot; {visibleStages.length} stages &middot; {totalPlans} plans
          </p>
        </div>

        <div className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden">
          <div className="flex gap-3 p-3 h-full items-stretch min-w-max">
            <NewLeadsColumn
              leadsCount={newLeadsCount}
              filtersArray={filtersArray}
              filterPlus={filterPlus}
              selectedLeadId={selection?.type === "lead" ? selection.lead.contactID : null}
              onSelectLead={handleSelectLead}
            />
            {visibleStages.map((stage) => (
              <StageColumn
                key={stage.accountCustomStageID}
                stage={stage}
                selectedPlanId={selection?.type === "plan" ? selection.plan.planID : null}
                onSelectPlan={handleSelectPlan}
                filtersArray={filtersArray}
                filterPlus={filterPlus}
              />
            ))}
          </div>
        </div>
      </div>
    </ListPageLayout>
    <LeadDetailModal
      isOpen={leadModalId !== null}
      onClose={() => setLeadModalId(null)}
      leadId={leadModalId}
    />
    </>
  );
}
